// App.tsx
import React, { useState } from 'react';
import DocumentList from './DocumentList';
import DocumentUpload from './DocumentUpload';
import MyDocuments from './MyDocuments';
import AiAssistant from './AiAssistant';
import ProposalDetail from './ProposalDetail';
import CrowdfundingDetail from './CrowdfundingDetail';
import CrowdfundingList from './CrowdfundingList';
import CrowdfundingFullList from './CrowdfundingFullList';
import TopContributors from './TopContributors';
import Events from './Events';

// Definim interfața pentru propunere
interface Proposal {
  id: number;
  titlu: string;
  descriere: string;
  voturi: number;
  vizualizari: number;
  comentarii: number;
  utilizator: string;
  timestamp: string;
  taguri: string[];
}

// Definim tipurile posibile de pagini
type PageType = 'list' | 'detail' | 'documentList' | 'documentUpload' | 'myDocuments' | 'aiAssistant' | 'crowdfunding-detail' | 'crowdfunding-list' | 'top-contributors' | 'events';

const App: React.FC = () => {
  // State pentru pagina curentă
  const [currentPage, setCurrentPage] = useState<PageType>('list');
  const [selectedProposalId, setSelectedProposalId] = useState<number | null>(null);
  
  // State pentru meniul mobil
  const [mobileMenuOpen, setMobileMenuOpen] = useState<boolean>(false);
  
  // State pentru lista de propuneri
  const [propuneri, setPropuneri] = useState<Proposal[]>([
    {
      id: 1,
      titlu: "Platformă digitală pentru colaborare între antreprenori locali",
      descriere: "Propun crearea unei platforme online dedicate conectării antreprenorilor din regiune pentru a facilita colaborări, parteneriate și schimb de resurse.",
      voturi: 15,
      vizualizari: 124,
      comentarii: 3,
      utilizator: "Maria Popescu",
      timestamp: "acum 2 ore",
      taguri: ["digitalizare", "colaborare", "networking"]
    },
    {
      id: 2,
      titlu: "Program de mentorat între antreprenorii cu experiență și startup-uri",
      descriere: "Un program structurat prin care antreprenorii cu experiență să ofere mentorat celor la început de drum, cu întâlniri lunare și obiective concrete.",
      voturi: 12,
      vizualizari: 98,
      comentarii: 5,
      utilizator: "Ion Marinescu",
      timestamp: "acum 5 ore",
      taguri: ["mentorat", "educație", "startup"]
    },
    {
      id: 3,
      titlu: "Fond de investiții comun pentru proiecte inovatoare locale",
      descriere: "Crearea unui fond de investiții la care să contribuie membrii patronatului și care să finanțeze proiecte inovatoare din comunitatea locală.",
      voturi: 8,
      vizualizari: 76,
      comentarii: 4,
      utilizator: "Elena Vasilescu",
      timestamp: "acum 1 zi",
      taguri: ["investiții", "inovație", "finanțare"]
    },
    {
      id: 4,
      titlu: "Târg anual de produse și servicii locale cu promovare națională",
      descriere: "Organizarea unui târg anual de amploare pentru promovarea produselor și serviciilor din regiunea noastră, cu invitați și promovare la nivel național.",
      voturi: 20,
      vizualizari: 210,
      comentarii: 7,
      utilizator: "Adrian Munteanu",
      timestamp: "acum 2 zile",
      taguri: ["marketing", "evenimente", "promovare"]
    },
    {
      id: 5,
      titlu: "Centru de resurse pentru tranziție la practici de afaceri sustenabile",
      descriere: "Un centru care să ofere consultanță, resurse și sprijin pentru afacerile care vor să adopte practici mai sustenabile și prietenoase cu mediul.",
      voturi: 7,
      vizualizari: 65,
      comentarii: 2,
      utilizator: "Cristina Dumitru",
      timestamp: "acum 3 zile",
      taguri: ["sustenabilitate", "mediu", "consultanță"]
    },
  ]);

  // State pentru filtrare
  const [filtruActiv, setFiltruActiv] = useState<string>("voturi");

  // Sortarea propunerilor în funcție de filtrul activ
  const propuneriSortate = [...propuneri].sort((a, b) => {
    if (filtruActiv === "voturi") return b.voturi - a.voturi;
    if (filtruActiv === "recente") return a.timestamp.localeCompare(b.timestamp);
    if (filtruActiv === "populare") return b.vizualizari - a.vizualizari;
    return 0;
  });

  // Funcție pentru a deschide o propunere
  const openProposalDetail = (proposalId: number): void => {
    setSelectedProposalId(proposalId);
    setCurrentPage('detail');
    // Închide meniul mobil dacă e deschis
    if (mobileMenuOpen) {
      setMobileMenuOpen(false);
    }
  };

  // Funcție pentru a deschide un proiect de crowdfunding
  const openCrowdfundingDetail = (projectId: number): void => {
    setSelectedProposalId(projectId);
    setCurrentPage('crowdfunding-detail');
    // Închide meniul mobil dacă e deschis
    if (mobileMenuOpen) {
      setMobileMenuOpen(false);
    }
  };
  
  // Funcție pentru a naviga la lista completă de crowdfunding
  const navigateToCrowdfundingList = (): void => {
    setCurrentPage('crowdfunding-list');
    if (mobileMenuOpen) {
      setMobileMenuOpen(false);
    }
  };

  // Funcție pentru a reveni la lista de propuneri
  const backToList = (): void => {
    setCurrentPage('list');
    setSelectedProposalId(null);
  };
  
  // Funcții pentru navigare la noile secțiuni
  const navigateToDocumentList = (): void => {
    setCurrentPage('documentList');
    if (mobileMenuOpen) {
      setMobileMenuOpen(false);
    }
  };
  
  const navigateToDocumentUpload = (): void => {
    setCurrentPage('documentUpload');
    if (mobileMenuOpen) {
      setMobileMenuOpen(false);
    }
  };
  
  const navigateToMyDocuments = (): void => {
    setCurrentPage('myDocuments');
    if (mobileMenuOpen) {
      setMobileMenuOpen(false);
    }
  };
  
  const navigateToAiAssistant = (): void => {
    setCurrentPage('aiAssistant');
    if (mobileMenuOpen) {
      setMobileMenuOpen(false);
    }
  };
  
  const navigateToTopContributors = (): void => {
    setCurrentPage('top-contributors');
    if (mobileMenuOpen) {
      setMobileMenuOpen(false);
    }
  };
  
  const navigateToEvents = (): void => {
    setCurrentPage('events');
    if (mobileMenuOpen) {
      setMobileMenuOpen(false);
    }
  };

  return (
    <div className="bg-gray-100 min-h-screen">
      {/* Header */}
      <header className="bg-white shadow">
        <div className="container mx-auto px-4 py-6">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-2">
              <div 
                className="text-blue-600 font-bold text-2xl cursor-pointer" 
                onClick={backToList}
              >PV</div>
              <h1 
                className="text-2xl font-bold cursor-pointer"
                onClick={backToList}
              >Patronat Vrancea</h1>
            </div>
            <div className="flex items-center space-x-4">
              <button 
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md font-medium"
                onClick={() => {
                  if (currentPage.includes('document')) {
                    navigateToDocumentUpload();
                  } else if (currentPage === 'crowdfunding-list' || currentPage === 'crowdfunding-detail') {
                    // Aici am putea adăuga o funcție pentru a adăuga un proiect nou de crowdfunding
                    alert('Inițiază un proiect nou de crowdfunding');
                  } else if (currentPage === 'events') {
                    alert('Propune un nou eveniment');
                  } else {
                    // Publică o propunere existentă
                  }
                }}
              >
                {currentPage.includes('document') 
                  ? 'Încarcă Document' 
                  : currentPage === 'crowdfunding-list' || currentPage === 'crowdfunding-detail'
                    ? 'Inițiază un proiect'
                    : currentPage === 'events'
                      ? 'Propune eveniment'
                      : 'Publică o propunere'
                }
              </button>
              <button 
                className="p-2 rounded-md hover:bg-gray-100"
                onClick={() => setMobileMenuOpen(true)}
              >
                <div className="w-6 h-0.5 bg-gray-600 mb-1"></div>
                <div className="w-6 h-0.5 bg-gray-600 mb-1"></div>
                <div className="w-6 h-0.5 bg-gray-600"></div>
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Meniu sandwich mobil */}
      <div className={`fixed inset-0 bg-black bg-opacity-50 z-50 ${mobileMenuOpen ? 'block' : 'hidden'}`}>
        <div className="bg-white h-full w-64 p-4">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-bold">Meniu</h2>
            <button className="p-2" onClick={() => setMobileMenuOpen(false)}>✕</button>
          </div>
          <ul className="space-y-4">
            <li>
              <a 
                href="#" 
                className={`flex items-center ${currentPage === 'list' ? 'text-blue-600 font-medium' : 'text-gray-700 hover:text-blue-600'}`}
                onClick={(e) => {
                  e.preventDefault();
                  backToList();
                }}
              >
                <span className="mr-2">🏠</span>
                Acasă
              </a>
            </li>
            <li>
              <a 
                href="#" 
                className={`flex items-center ${currentPage === 'documentList' ? 'text-blue-600 font-medium' : 'text-gray-700 hover:text-blue-600'}`}
                onClick={(e) => {
                  e.preventDefault();
                  navigateToDocumentList();
                }}
              >
                <span className="mr-2">📄</span>
                Documente
              </a>
            </li>
            <li>
              <a 
                href="#" 
                className={`flex items-center ${currentPage === 'aiAssistant' ? 'text-blue-600 font-medium' : 'text-gray-700 hover:text-blue-600'}`}
                onClick={(e) => {
                  e.preventDefault();
                  navigateToAiAssistant();
                }}
              >
                <span className="mr-2">🤖</span>
                Asistent AI
              </a>
            </li>
            <li>
              <a 
                href="#" 
                className={`flex items-center ${currentPage === 'events' ? 'text-blue-600 font-medium' : 'text-gray-700 hover:text-blue-600'}`}
                onClick={(e) => {
                  e.preventDefault();
                  navigateToEvents();
                }}
              >
                <span className="mr-2">🗓️</span>
                Evenimente
              </a>
            </li>
            <li>
              <a 
                href="#" 
                className={`flex items-center ${currentPage === 'crowdfunding-list' ? 'text-blue-600 font-medium' : 'text-gray-700 hover:text-blue-600'}`}
                onClick={(e) => {
                  e.preventDefault();
                  navigateToCrowdfundingList();
                }}
              >
                <span className="mr-2">📝</span>
                Propuneri
              </a>
            </li>
            <li>
              <a 
                href="#" 
                className={`flex items-center ${currentPage === 'top-contributors' ? 'text-blue-600 font-medium' : 'text-gray-700 hover:text-blue-600'}`}
                onClick={(e) => {
                  e.preventDefault();
                  navigateToTopContributors();
                }}
              >
                <span className="mr-2">🏆</span>
                Top Contribuitori
              </a>
            </li>
            <li className="mt-8">
              <button className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md font-medium w-full">
                {currentPage.includes('document') ? 'Încarcă Document' : 'Publică o propunere'}
              </button>
            </li>
            <li className="mt-2">
              <button className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-md font-medium w-full">
                Inițiază un proiect
              </button>
            </li>
          </ul>
        </div>
      </div>
      
      <main className="container mx-auto px-4 py-6">
        <div className="flex flex-col md:flex-row gap-6">
          {/* Conținut principal */}
          <div className="w-full md:w-1/4 md:block hidden">
            <div className="bg-white rounded-lg shadow p-4 mb-4">
              <h2 className="text-lg font-semibold mb-4">Navigare</h2>
              <ul className="space-y-2">
                <li>
                  <a 
                    href="#" 
                    className={`flex items-center ${currentPage === 'list' ? 'text-blue-600 font-medium' : 'text-gray-700 hover:text-blue-600'}`}
                    onClick={(e) => {
                      e.preventDefault();
                      backToList();
                    }}
                  >
                    <span className="mr-2">🏠</span>
                    Acasă
                  </a>
                </li>
                <li>
                  <a 
                    href="#" 
                    className={`flex items-center ${currentPage === 'documentList' ? 'text-blue-600 font-medium' : 'text-gray-700 hover:text-blue-600'}`}
                    onClick={(e) => {
                      e.preventDefault();
                      navigateToDocumentList();
                    }}
                  >
                    <span className="mr-2">📄</span>
                    Documente
                  </a>
                </li>
                <li>
                  <a 
                    href="#" 
                    className={`flex items-center ${currentPage === 'aiAssistant' ? 'text-blue-600 font-medium' : 'text-gray-700 hover:text-blue-600'}`}
                    onClick={(e) => {
                      e.preventDefault();
                      navigateToAiAssistant();
                    }}
                  >
                    <span className="mr-2">🤖</span>
                    Asistent AI
                  </a>
                </li>
                <li>
                  <a 
                    href="#" 
                    className={`flex items-center ${currentPage === 'events' ? 'text-blue-600 font-medium' : 'text-gray-700 hover:text-blue-600'}`}
                    onClick={(e) => {
                      e.preventDefault();
                      navigateToEvents();
                    }}
                  >
                    <span className="mr-2">🗓️</span>
                    Evenimente
                  </a>
                </li>
                <li>
                  <a 
                    href="#" 
                    className={`flex items-center ${currentPage === 'crowdfunding-list' ? 'text-blue-600 font-medium' : 'text-gray-700 hover:text-blue-600'}`}
                    onClick={(e) => {
                      e.preventDefault();
                      navigateToCrowdfundingList();
                    }}
                  >
                    <span className="mr-2">📝</span>
                    Propuneri
                  </a>
                </li>
                <li>
                  <a 
                    href="#" 
                    className={`flex items-center ${currentPage === 'top-contributors' ? 'text-blue-600 font-medium' : 'text-gray-700 hover:text-blue-600'}`}
                    onClick={(e) => {
                      e.preventDefault();
                      navigateToTopContributors();
                    }}
                  >
                    <span className="mr-2">🏆</span>
                    Top Contribuitori
                  </a>
                </li>
              </ul>
            </div>
            
            {/* Submeniu pentru documente - apare doar când suntem în secțiunea documente */}
            {currentPage.includes('document') && (
              <div className="bg-white rounded-lg shadow p-4 mb-4">
                <h2 className="text-lg font-semibold mb-4">Documente</h2>
                <ul className="space-y-2">
                  <li>
                    <a 
                      href="#" 
                      className={`flex items-center ${currentPage === 'documentList' ? 'text-blue-600 font-medium' : 'text-gray-700 hover:text-blue-600'}`}
                      onClick={(e) => {
                        e.preventDefault();
                        navigateToDocumentList();
                      }}
                    >
                      <span className="mr-2">📚</span>
                      Toate documentele
                    </a>
                  </li>
                  <li>
                    <a 
                      href="#" 
                      className={`flex items-center ${currentPage === 'myDocuments' ? 'text-blue-600 font-medium' : 'text-gray-700 hover:text-blue-600'}`}
                      onClick={(e) => {
                        e.preventDefault();
                        navigateToMyDocuments();
                      }}
                    >
                      <span className="mr-2">📂</span>
                      Documentele mele
                    </a>
                  </li>
                  <li>
                    <a 
                      href="#" 
                      className={`flex items-center ${currentPage === 'documentUpload' ? 'text-blue-600 font-medium' : 'text-gray-700 hover:text-blue-600'}`}
                      onClick={(e) => {
                        e.preventDefault();
                        navigateToDocumentUpload();
                      }}
                    >
                      <span className="mr-2">📤</span>
                      Încarcă document
                    </a>
                  </li>
                </ul>
              </div>
            )}

            <div className="bg-white rounded-lg shadow p-4">
              <h2 className="text-lg font-semibold mb-4">Acțiuni rapide</h2>
              <div className="space-y-2">
                <button 
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white px-3 py-2 rounded-md font-medium text-sm"
                  onClick={() => {
                    if (currentPage === 'crowdfunding-list') {
                      alert('Inițiază un proiect nou');
                    } else if (currentPage === 'events') {
                      alert('Propune un nou eveniment');
                    }
                  }}
                >
                  {currentPage === 'crowdfunding-list' || currentPage === 'crowdfunding-detail' 
                    ? 'Inițiază un proiect nou' 
                    : currentPage === 'events'
                      ? 'Propune eveniment'
                      : 'Publică o propunere'
                  }
                </button>
                <button 
                  className="w-full bg-green-600 hover:bg-green-700 text-white px-3 py-2 rounded-md font-medium text-sm"
                  onClick={() => {
                    if (currentPage !== 'events') {
                      navigateToCrowdfundingList();
                    } else {
                      navigateToEvents();
                    }
                  }}
                >
                  {currentPage === 'events'
                    ? 'Vezi toate evenimentele'
                    : 'Vezi toate proiectele'
                  }
                </button>
              </div>
            </div>
          </div>

          {/* Content Area */}
          <div className="w-full md:w-3/4">
            {currentPage === 'list' && (
              // Pagina principală
              <>
                {/* Secțiunea de crowdfunding */}
                <CrowdfundingList onOpenProject={openCrowdfundingDetail} />
              
                {/* Lista de propuneri */}
                <div className="bg-white rounded-lg shadow mb-4">
                  <div className="p-4 border-b">
                    <h2 className="text-xl font-semibold">Explorează propunerile antreprenorilor</h2>
                    <p className="text-gray-600">Idei, inițiative și soluții propuse de comunitatea antreprenorială</p>
                  </div>

                  <div className="p-4 flex items-center justify-between">
                    <div className="flex gap-2">
                      <button 
                        className={`px-3 py-1 rounded-full text-sm ${filtruActiv === 'voturi' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100'}`}
                        onClick={() => setFiltruActiv('voturi')}
                      >
                        Cele mai votate
                      </button>
                      <button 
                        className={`px-3 py-1 rounded-full text-sm ${filtruActiv === 'recente' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100'}`}
                        onClick={() => setFiltruActiv('recente')}
                      >
                        Recente
                      </button>
                      <button 
                        className={`px-3 py-1 rounded-full text-sm ${filtruActiv === 'populare' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100'}`}
                        onClick={() => setFiltruActiv('populare')}
                      >
                        Populare
                      </button>
                    </div>
                    <div>
                      <span className="text-sm text-gray-500">1 propunere cu activitate nouă</span>
                    </div>
                  </div>
                </div>

                {/* Proposal list */}
                <div className="space-y-4">
                  {propuneriSortate.map((propunere) => (
                    <div 
                      key={propunere.id} 
                      className="bg-white rounded-lg shadow p-4 cursor-pointer hover:shadow-md transition-shadow"
                      onClick={() => openProposalDetail(propunere.id)}
                    >
                      <div className="flex">
                        <div className="flex flex-col items-center mr-6">
                          <button 
                            className="flex flex-col items-center p-2 rounded-md hover:bg-gray-100"
                            onClick={(e) => {
                              e.stopPropagation(); // Previne deschiderea detaliilor la click pe vot
                              const newPropuneri = [...propuneri];
                              const index = newPropuneri.findIndex(i => i.id === propunere.id);
                              newPropuneri[index].voturi += 1;
                              setPropuneri(newPropuneri);
                            }}
                          >
                            <span className="text-xl">▲</span>
                            <span className="font-semibold">{propunere.voturi}</span>
                          </button>
                          <div className="mt-2 text-center">
                            <div className="text-sm text-gray-500">{propunere.comentarii}</div>
                            <div className="text-xs text-gray-500">comentarii</div>
                          </div>
                          <div className="mt-2 text-center">
                            <div className="text-sm text-gray-500">{propunere.vizualizari}</div>
                            <div className="text-xs text-gray-500">vizualizări</div>
                          </div>
                        </div>

                        <div className="flex-1">
                          <h3 className="text-lg font-semibold text-blue-600 mb-2">
                            {propunere.titlu}
                          </h3>
                          <p className="text-gray-700 mb-3">{propunere.descriere}</p>
                          <div className="flex flex-wrap gap-2 mb-3">
                            {propunere.taguri.map((tag, index) => (
                              <a 
                                key={index} 
                                href="#" 
                                className="px-2 py-1 bg-gray-100 text-xs rounded-md hover:bg-gray-200"
                                onClick={(e) => e.stopPropagation()}
                              >
                                {tag}
                              </a>
                            ))}
                          </div>
                          <div className="flex items-center justify-end text-sm text-gray-500">
                            <div className="flex items-center">
                              <span className="text-sm mr-2">👤</span>
                              <span>{propunere.utilizator}</span>
                            </div>
                            <div className="ml-4 flex items-center">
                              <span className="text-sm mr-1">⏱️</span>
                              <span>{propunere.timestamp}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
            
            {currentPage === 'detail' && (
              // Pagina de detalii propunere
              <ProposalDetail onBack={backToList} />
            )}
            
            {currentPage === 'crowdfunding-detail' && (
              // Pagina de detalii crowdfunding
              <CrowdfundingDetail onBack={backToList} />
            )}
            
            {currentPage === 'crowdfunding-list' && (
              // Pagina cu lista completă de proiecte crowdfunding
              <CrowdfundingFullList onOpenProject={openCrowdfundingDetail} />
            )}
            
            {currentPage === 'documentList' && (
              // Lista de documente
              <DocumentList />
            )}
            
            {currentPage === 'documentUpload' && (
              // Pagina pentru încărcarea documentelor
              <DocumentUpload onBack={navigateToDocumentList} />
            )}
            
            {currentPage === 'myDocuments' && (
              // Pagina cu documentele mele
              <MyDocuments onBack={navigateToDocumentList} />
            )}
            
            {currentPage === 'aiAssistant' && (
              // Pagina asistentului AI
              <AiAssistant />
            )}
            
            {currentPage === 'top-contributors' && (
              // Pagina Top Contribuitori
              <TopContributors />
            )}
            
            {currentPage === 'events' && (
              // Pagina Evenimente
              <Events />
            )}
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-gray-800 text-white py-6">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="mb-4 md:mb-0">
              <h2 className="text-xl font-bold">Patronat Vrancea</h2>
              <p className="text-gray-400">Conectăm antreprenorii din comunitatea locală</p>
            </div>
            <div className="flex gap-4">
              <a href="#" className="text-gray-400 hover:text-white">Despre noi</a>
              <a href="#" className="text-gray-400 hover:text-white">Contact</a>
              <a href="#" className="text-gray-400 hover:text-white">Termeni și condiții</a>
              <a href="#" className="text-gray-400 hover:text-white">Confidențialitate</a>
            </div>
          </div>
          
          {/* Categorii populare în footer */}
          {/* Categorii populare în footer */}
          <div className="mt-8 border-t border-gray-700 pt-6">
            <h3 className="text-lg font-semibold mb-4">Categorii populare</h3>
            <div className="flex flex-wrap gap-3">
              <a href="#" className="px-3 py-1 bg-gray-700 text-sm rounded-full hover:bg-gray-600">marketing</a>
              <a href="#" className="px-3 py-1 bg-gray-700 text-sm rounded-full hover:bg-gray-600">finanțare</a>
              <a href="#" className="px-3 py-1 bg-gray-700 text-sm rounded-full hover:bg-gray-600">taxe</a>
              <a href="#" className="px-3 py-1 bg-gray-700 text-sm rounded-full hover:bg-gray-600">resurse umane</a>
              <a href="#" className="px-3 py-1 bg-gray-700 text-sm rounded-full hover:bg-gray-600">inovație</a>
              <a href="#" className="px-3 py-1 bg-gray-700 text-sm rounded-full hover:bg-gray-600">vânzări</a>
              <a href="#" className="px-3 py-1 bg-gray-600 text-gray-300 text-sm rounded-full hover:bg-gray-500">Vezi toate</a>
            </div>
          </div>
          
          <div className="mt-6 text-center text-sm text-gray-400">
            © 2025 Patronat Vrancea. Toate drepturile rezervate.
          </div>
        </div>
      </footer>
    </div>
  );
};

export default App;